import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Send, X, Loader2, Bot, User as UserIcon, FileText, MessageSquare, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { v2Api } from "@/lib/v2Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PAGE_ASK_EVENT, PAGE_ASK_CLEAR } from "@/lib/pageAskRegistry";

function useSidebarState() {
  const [unread, setUnread] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    const onUnread = (e) => setUnread(e.detail?.unread ?? 0);
    const onState = (e) => setSidebarOpen(e.detail?.mode !== "hidden");
    window.addEventListener("v3.chatSidebar.unread", onUnread);
    window.addEventListener("v3.chatSidebar.state", onState);
    return () => {
      window.removeEventListener("v3.chatSidebar.unread", onUnread);
      window.removeEventListener("v3.chatSidebar.state", onState);
    };
  }, []);
  return { unread, sidebarOpen };
}

/** Listen for page-level Ask registrations from AskWidget / CatalogAskWidget */
function usePageAsk() {
  const [pageAsk, setPageAsk] = useState(null); // { title, buttonColor, onOpen }
  useEffect(() => {
    const onRegister = (e) => setPageAsk(e.detail);
    const onClear = () => setPageAsk(null);
    window.addEventListener(PAGE_ASK_EVENT, onRegister);
    window.addEventListener(PAGE_ASK_CLEAR, onClear);
    return () => {
      window.removeEventListener(PAGE_ASK_EVENT, onRegister);
      window.removeEventListener(PAGE_ASK_CLEAR, onClear);
    };
  }, []);
  return pageAsk;
}

/** Floating chat widget — auto-detects /v2/projects/:id route and
 * lets the user scope to All RFPs / current project / a specific RFP.
 * Also hosts any page-level Ask button registered via pageAskRegistry. */
export default function ChatWidget() {
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [rfps, setRfps] = useState([]);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("all"); // "all" | "project" | "rfp"
  const [scopeRfpId, setScopeRfpId] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const pageAsk = usePageAsk();

  // Auto-detect project context from URL like /v2/projects/<uuid>
  useEffect(() => {
    const m = location.pathname.match(/^\/v2\/projects\/([0-9a-f-]{36})/i);
    if (!m) {
      setProject(null);
      setRfps([]);
      return;
    }
    const projectId = m[1];
    Promise.allSettled([
      v2Api.projects.get(projectId),
      v2Api.rfp.listForProject(projectId),
    ]).then(([p, r]) => {
      if (p.status === "fulfilled") setProject(p.value);
      if (r.status === "fulfilled") setRfps(r.value || []);
    });
  }, [location.pathname]);

  // When project context appears, default scope to that project
  useEffect(() => {
    if (project?.id && scope === "all") setScope("project");
    // eslint-disable-next-line
  }, [project?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    if (e) e.preventDefault();
    const q = draft.trim();
    if (!q) return;
    setDraft("");
    // Capture history BEFORE adding the new user message.
    const historySnapshot = messages.map((m) => ({ role: m.role, content: m.text }));
    const userMsg = { role: "user", text: q, ts: Date.now() };
    setMessages((m) => [...m, userMsg]);
    setBusy(true);
    try {
      const payload = { query: q, k: 8, history: historySnapshot };
      if (scope === "project" && project?.id) payload.project_id = project.id;
      if (scope === "rfp" && scopeRfpId) payload.rfp_id = scopeRfpId;
      const res = await v2Api.chat(payload);
      setMessages((m) => [...m, {
        role: "assistant",
        text: res.answer || "(empty)",
        citations: res.citations || [],
        model: res.model,
        ts: Date.now(),
      }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: "(error: " + (err?.response?.data?.detail ?? err.message) + ")",
        ts: Date.now(),
      }]);
    } finally {
      setBusy(false);
    }
  };

  const { unread: sidebarUnread, sidebarOpen } = useSidebarState();

  const scopeLabel = (() => {
    if (scope === "all") return "All RFPs";
    if (scope === "project") return project?.name ? `Project: ${project.name}` : "Current project";
    if (scope === "rfp") {
      const r = rfps.find((x) => x.id === scopeRfpId);
      return r ? `RFP: ${r.title}` : "Pick an RFP";
    }
    return "";
  })();

  if (!open) {
    return (
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {/* Page-level Ask button — registered by AskWidget/CatalogAskWidget on the current page */}
        {pageAsk && (
          <button
            type="button"
            onClick={pageAsk.onOpen}
            className={`flex items-center gap-2 ${pageAsk.buttonColor} text-white rounded-full px-4 py-2.5 shadow-lg transition-colors`}
            title={pageAsk.title}
          >
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">{pageAsk.title}</span>
          </button>
        )}

        {/* Channels toggle — hidden when sidebar is already open */}
        {!sidebarOpen && (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("v3.chatSidebar.toggle"))}
            className="relative flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white rounded-full px-4 py-2.5 shadow-lg"
            title="Open channels"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="text-sm font-medium">Channels</span>
            {sidebarUnread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
                {sidebarUnread > 99 ? "99+" : sidebarUnread}
              </span>
            )}
          </button>
        )}

      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[420px] max-w-[95vw] h-[600px] max-h-[80vh] bg-white border border-slate-200 rounded-lg shadow-xl flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50 rounded-t-lg">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-slate-800">Ask RFPs</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              type="button"
              onClick={() => setMessages([])}
              className="text-slate-400 hover:text-slate-600 p-1 rounded"
              title="New chat"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-slate-700 p-1 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="px-3 py-2 border-b bg-white space-y-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">Scope</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={() => setScope("all")}
            className={`text-xs px-2 py-0.5 rounded-full border ${scope === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
          >
            All RFPs
          </button>
          {project?.id && (
            <button
              type="button"
              onClick={() => setScope("project")}
              className={`text-xs px-2 py-0.5 rounded-full border ${scope === "project" ? "bg-blue-600 text-white border-blue-600" : "bg-blue-50 text-blue-700 border-blue-200 hover:border-blue-400"}`}
            >
              This project
            </button>
          )}
          {rfps.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => { setScope("rfp"); if (!scopeRfpId && rfps[0]) setScopeRfpId(rfps[0].id); }}
                className={`text-xs px-2 py-0.5 rounded-full border ${scope === "rfp" ? "bg-emerald-600 text-white border-emerald-600" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-400"}`}
              >
                Single RFP
              </button>
              {scope === "rfp" && (
                <select
                  value={scopeRfpId}
                  onChange={(e) => setScopeRfpId(e.target.value)}
                  className="text-xs border border-slate-200 rounded px-1 py-0.5 max-w-[180px] bg-white"
                >
                  <option value="">Pick…</option>
                  {rfps.map((r) => (
                    <option key={r.id} value={r.id}>{r.title?.slice(0, 60) || r.id}</option>
                  ))}
                </select>
              )}
            </>
          )}
        </div>
        <div className="text-[10px] text-slate-400 truncate">{scopeLabel}</div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50">
        {messages.length === 0 && (
          <div className="text-center text-sm text-slate-400 py-10">
            Ask anything about the RFPs in scope.<br />
            <span className="text-xs">e.g. "Which RFPs require NABL accreditation?"</span>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4" />
              </div>
            )}
            <div className={`max-w-[80%] ${m.role === "user" ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-800"} rounded-lg px-3 py-2 text-sm`}>
              <div className="whitespace-pre-wrap">{m.text}</div>
              {m.citations && m.citations.length > 0 && /\[\d+\]/.test(m.text) && (
                <div className="mt-2 pt-2 border-t border-slate-200 space-y-1">
                  {m.citations.map((c, j) => (
                    <div key={j} className="text-[11px] text-slate-600 flex items-start gap-1">
                      <span className="font-mono text-slate-400 shrink-0">[{j + 1}]</span>
                      <FileText className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-slate-700 font-medium truncate" title={c.rfp_title || ""}>
                          {c.rfp_id ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                v2Api.rfp.openFileInNewTab(c.rfp_id, c.page).catch(() => {
                                  toast.error("Could not open file (auth/file missing)");
                                });
                              }}
                              className="text-blue-600 hover:underline text-left"
                              title={`Open ${c.rfp_title || "RFP"} at page ${c.page}`}
                            >
                              p.{c.page} · {c.rfp_title || "RFP"} ↗
                            </button>
                          ) : (
                            <>p.{c.page} · {c.rfp_title || "RFP"}</>
                          )}
                        </div>
                        <div className="text-slate-500 line-clamp-2">{c.snippet}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {m.role === "user" && (
              <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center shrink-0">
                <UserIcon className="w-4 h-4" />
              </div>
            )}
          </div>
        ))}
        {busy && (
          <div className="flex gap-2 justify-start">
            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
              <Bot className="w-4 h-4" />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
              <span className="text-slate-500 text-xs">Searching…</span>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="p-2 border-t bg-white flex items-center gap-2 rounded-b-lg">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask about the RFPs in scope…"
          disabled={busy}
          className="flex-1"
        />
        <Button type="submit" disabled={busy || !draft.trim()} size="sm">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
